import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';

interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  role: string;
  status: string;
  profile: {
    dateOfBirth?: string;
    country?: string;
  };
  legalCompliance: {
    isMinor: boolean;
    ageVerified: boolean;
    coppaCompliant: boolean;
    gdprConsent: {
      dataProcessing: boolean;
      marketing: boolean;
      analytics: boolean;
      consentDate: string;
    };
    rightToBeForgetton: {
      requested: boolean;
      requestDate?: string;
      processed: boolean;
    };
  };
  createdAt: string;
  lastActiveAt: string;
}

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    role: '',
    isMinor: '',
    hasConsent: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [users, filters]);

  const fetchUsers = async () => {
    try {
      const response = await apiClient.get('/v1/users');
      setUsers(response.data);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...users];

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(
        user =>
          user.username.toLowerCase().includes(searchTerm) ||
          user.email.toLowerCase().includes(searchTerm) ||
          user.firstName.toLowerCase().includes(searchTerm) ||
          user.lastName.toLowerCase().includes(searchTerm)
      );
    }

    if (filters.status) {
      filtered = filtered.filter(user => user.status === filters.status);
    }

    if (filters.role) {
      filtered = filtered.filter(user => user.role === filters.role);
    }

    if (filters.isMinor) {
      filtered = filtered.filter(user => 
        filters.isMinor === 'true' ? user.legalCompliance?.isMinor : !user.legalCompliance?.isMinor
      );
    }

    if (filters.hasConsent) {
      filtered = filtered.filter(user => 
        filters.hasConsent === 'true' 
          ? user.legalCompliance?.gdprConsent?.dataProcessing 
          : !user.legalCompliance?.gdprConsent?.dataProcessing
      );
    }

    setFilteredUsers(filtered);
  };

  const updateUserStatus = async (userId: string, newStatus: string) => {
    try {
      await apiClient.put(`/v1/users/${userId}`, { status: newStatus });
      await fetchUsers();
    } catch (error) {
      alert('Error updating user status: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const requestDataDeletion = async (userId: string) => {
    if (!window.confirm('Are you sure you want to request data deletion for this user?')) {
      return;
    }

    try {
      await apiClient.post(`/v1/users/${userId}/request-deletion`);
      await fetchUsers();
      alert('Data deletion request submitted successfully');
    } catch (error) {
      alert('Error requesting data deletion: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const exportUserData = async (userId: string) => {
    try {
      const response = await apiClient.get(`/v1/users/${userId}/export-data`);
      const data = response.data;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-data-${userId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      alert('Error exporting user data: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const calculateAge = (dateOfBirth: string) => {
    const today = new Date();
    const birth = new Date(dateOfBirth);
    const age = Math.floor((today.getTime() - birth.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
    return age;
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-page-header">
          <h1 className="admin-page-title">User Management</h1>
          <p className="admin-page-description">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="admin-page-header">
          <h1 className="admin-page-title">User Management</h1>
          <p className="admin-page-description">Error loading users</p>
        </div>
        <div className="admin-alert danger">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">User Management</h1>
        <p className="admin-page-description">
          Manage users, compliance status, and data privacy
        </p>
      </div>

      {/* Filters */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Filters</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <label>Search:</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Username, email, or name"
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            />
          </div>
          <div>
            <label>Status:</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <div>
            <label>Role:</label>
            <select
              value={filters.role}
              onChange={(e) => setFilters({ ...filters, role: e.target.value })}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            >
              <option value="">All</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="moderator">Moderator</option>
            </select>
          </div>
          <div>
            <label>Minor Status:</label>
            <select
              value={filters.isMinor}
              onChange={(e) => setFilters({ ...filters, isMinor: e.target.value })}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            >
              <option value="">All</option>
              <option value="true">Minors Only</option>
              <option value="false">Adults Only</option>
            </select>
          </div>
          <div>
            <label>Consent Status:</label>
            <select
              value={filters.hasConsent}
              onChange={(e) => setFilters({ ...filters, hasConsent: e.target.value })}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
            >
              <option value="">All</option>
              <option value="true">With Consent</option>
              <option value="false">Without Consent</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Users ({filteredUsers.length})</h2>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Status</th>
                <th>Role</th>
                <th>Age</th>
                <th>Country</th>
                <th>Consent</th>
                <th>Last Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div>
                      <strong>{user.displayName}</strong>
                      <br />
                      <small>@{user.username}</small>
                      <br />
                      <small>{user.email}</small>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${user.status}`}>
                      {user.status}
                    </span>
                    {user.legalCompliance?.isMinor && (
                      <span className="status-badge minor" style={{ marginLeft: '0.5rem' }}>
                        Minor
                      </span>
                    )}
                  </td>
                  <td>{user.role}</td>
                  <td>
                    {user.profile?.dateOfBirth 
                      ? calculateAge(user.profile.dateOfBirth) 
                      : 'Unknown'}
                  </td>
                  <td>{user.profile?.country || 'Unknown'}</td>
                  <td>
                    <div style={{ fontSize: '0.8rem' }}>
                      <div>Data: {user.legalCompliance?.gdprConsent?.dataProcessing ? '✅' : '❌'}</div>
                      <div>Marketing: {user.legalCompliance?.gdprConsent?.marketing ? '✅' : '❌'}</div>
                      <div>Analytics: {user.legalCompliance?.gdprConsent?.analytics ? '✅' : '❌'}</div>
                    </div>
                  </td>
                  <td>{formatDate(user.lastActiveAt)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                      <select
                        value={user.status}
                        onChange={(e) => updateUserStatus(user.id, e.target.value)}
                        style={{ fontSize: '0.8rem', padding: '0.25rem' }}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="suspended">Suspended</option>
                      </select>
                      <button
                        className="admin-button secondary"
                        onClick={() => exportUserData(user.id)}
                        style={{ fontSize: '0.8rem', padding: '0.25rem' }}
                      >
                        Export
                      </button>
                      {user.legalCompliance?.rightToBeForgetton?.requested ? (
                        <span style={{ fontSize: '0.8rem', color: '#d32f2f' }}>
                          Deletion Requested
                        </span>
                      ) : (
                        <button
                          className="admin-button danger"
                          onClick={() => requestDataDeletion(user.id)}
                          style={{ fontSize: '0.8rem', padding: '0.25rem' }}
                        >
                          Request Deletion
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;